import api from "./api"
import { SagePayrollExportPreview } from "../Interfaces"
import {
  messageFromBlobError,
  SageApiError,
  throwSageApiError,
} from "../utils/sageApiError"
import { parseContentDispositionFilename, payrollFilenameFallback } from "../utils/payrollExport"

export type PayrollExportFormat = "csv" | "xlsx"

function companyPayrollUrl(companyId: number) {
  return `/sage-100/companies/${companyId}/payroll-export`
}

function triggerBrowserDownload(blob: Blob, filename: string) {
  const href = window.URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = href
  link.download = filename
  document.body.appendChild(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(href)
}

class SagePayrollExportService {
  public preview = async (
    companyId: number,
    options: { from: string, to: string },
  ): Promise<SagePayrollExportPreview> => {
    if (!companyId) throw new Error("No management company id provided")
    try {
      const response = await api.get(companyPayrollUrl(companyId), {
        params: { from: options.from, to: options.to, preview: true },
      })
      const payload = response.data || {}
      return {
        success: payload.success !== false,
        companyId: payload.companyId ?? companyId,
        companyCode: payload.companyCode,
        from: payload.from ?? options.from,
        to: payload.to ?? options.to,
        filename: payload.filename,
        rowCount: payload.rowCount ?? (Array.isArray(payload.rows) ? payload.rows.length : 0),
        rows: Array.isArray(payload.rows) ? payload.rows : [],
        warnings: Array.isArray(payload.warnings) ? payload.warnings : [],
        error: payload.error,
        message: payload.message,
      }
    } catch (error) {
      throwSageApiError(error, "Could not load the payroll export. Try again.")
    }
  }

  public download = async (
    companyId: number,
    options: { from: string, to: string, format?: PayrollExportFormat, companyCode?: string },
  ): Promise<{ filename: string }> => {
    if (!companyId) throw new Error("No management company id provided")
    const format = options.format === "xlsx" ? "xlsx" : "csv"
    try {
      const params: { from: string, to: string, format?: PayrollExportFormat } = {
        from: options.from,
        to: options.to,
      }
      if (format === "xlsx") params.format = "xlsx"
      const response = await api.get(companyPayrollUrl(companyId), {
        params,
        responseType: "blob",
      })
      const filename =
        parseContentDispositionFilename(response.headers?.["content-disposition"])
        || payrollFilenameFallback(options.companyCode || "", options.from, options.to, format)
      triggerBrowserDownload(response.data, filename)
      return { filename }
    } catch (error) {
      const message = (await messageFromBlobError(error)).trim()
      throw new SageApiError(
        (error as { response?: { status?: number } })?.response?.status ?? 500,
        message || "Could not download the payroll file.",
      )
    }
  }
}

export const sagePayrollExportService = new SagePayrollExportService()
